import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from './UserContext';
import * as api from '../services/api';

const FeedContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useFeed = () => {
  const context = useContext(FeedContext);
  if (!context) {
    throw new Error('useFeed must be used within a FeedProvider');
  }
  return context;
};

export const FeedProvider = ({ children }) => {
  const { user } = useUser();
  return <FeedSession key={user?.id ?? 'guest'} userId={user?.id}>{children}</FeedSession>;
};

const FeedSession = ({ children, userId }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const page = useRef(0);
  const [hasMore, setHasMore] = useState(true);

  const generation = useRef(0);
  const pending = useRef(false);
  const moreAvailable = useRef(true);
  const snapshot = useRef(null);
  const session = useRef(null);
  const hiddenUsers = useRef(new Set());

  // Загрузить ленту активности
  const loadFeed = useCallback(async (pageNum = 1, append = false) => {
    if (!userId || (append && (pending.current || !moreAvailable.current))) return;
    const request = ++generation.current;
    pending.current = true;
    
    setLoading(true);
    setError(null);
    
    try {
      let response;
      try {
        response = await api.getFeed({ page: pageNum, limit: 20, include_recommended: 'true', ...(append && snapshot.current != null ? { snapshot: snapshot.current } : {}), ...(append && session.current ? { feed_session: session.current } : {}) });
      } catch (err) {
        if (!append || err.status !== 410) throw err;
        if (request !== generation.current) return;
        append = false;
        pageNum = 1;
        response = await api.getFeed({ page: 1, limit: 20, include_recommended: 'true' });
      }
      
      let data = Array.isArray(response) ? response : (response.activities || []);
      const hasMoreFromServer = data.length > 0 && (!Array.isArray(response) ? response.has_more : data.length >= 20);
      
      if (request !== generation.current) return;
      snapshot.current = response.snapshot ?? null;
      session.current = response.feed_session ?? null;
      data = data.filter(a => !hiddenUsers.current.has(a.user_id));

      setActivities(prev => {
        const base = append ? prev : [];
        const seen = new Set(base.map(a => String(a.id)));
        const added = data.filter(a => {
          const id = String(a.id);
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        });
        return append && !added.length ? prev : [...base, ...added];
      });
      
      moreAvailable.current = hasMoreFromServer;
      setHasMore(hasMoreFromServer);
      page.current = response.page ?? pageNum;
    } catch (err) {
      if (request !== generation.current) return;
      console.error('Ошибка загрузки ленты:', err);
      setError(err.message);
    } finally {
      if (request === generation.current) { pending.current = false; setLoading(false); }
    }
  }, [userId]);

  // Загрузить следующую страницу
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      return loadFeed(page.current + 1, true);
    }
  }, [loading, hasMore, loadFeed]);

  // Обновить ленту
  const refresh = useCallback(() => {
    return loadFeed(1, false);
  }, [loadFeed]);

  // Скрыть пользователя из рекомендаций (кнопка "Не интересно")
  const dismissUser = useCallback(async (dismissedUserId) => {
    try {
      await api.dismissFeedUser(dismissedUserId);
      hiddenUsers.current.add(dismissedUserId);
      // Удаляем все посты этого пользователя из ленты
      setActivities(prev => prev.filter(a => a.user_id !== dismissedUserId));
      await loadFeed(1, false);
    } catch (err) {
      console.error('Ошибка скрытия пользователя:', err);
      setError(err.message);
      throw err;
    }
  }, [loadFeed]);

  // Загружать при монтировании
  useEffect(() => {
    const requests = generation;
    if (userId) loadFeed();
    return () => { requests.current++; };
  }, [userId, loadFeed]);

  const value = {
    activities,
    loading,
    error,
    hasMore,
    loadFeed,
    loadMore,
    refresh,
    dismissUser
  };

  return (
    <FeedContext.Provider value={value}>
      {children}
    </FeedContext.Provider>
  );
};

export default FeedContext;
